import React, { useEffect, useMemo, useState } from 'react';
import './DualListBox.css';
import api from '../../Services/api';
import { loadSubgroupOptions, SubgroupOption } from './mapItemDualListRows';

interface Item {
  id: string;
  label: string;
  ite_alternativeidentifier?: string;
  its_id?: string;
  ite_tag?: string;
  its_description?: string;
}

type IndexedItem = Item & {
  labelLower: string;
  altLower: string;
  itsId: string;
};

type ListFilters = {
  description: string;
  alternative: string;
  subgroupId: string;
};

interface DualListBoxProps {
  items: Item[];
  itemsSel: Item[];
  pegarItens: any;
  descricao1: any;
  descricao2: any;
  enableItemFieldSearch?: boolean;
}

const EMPTY_LIMIT = 100;
const PAGE_SIZE = 200;
const DEBOUNCE_MS = 300;

function indexItems(items: Item[]): IndexedItem[] {
  return items.map((item) => ({
    ...item,
    ite_alternativeidentifier: item.ite_alternativeidentifier || '',
    its_id: item.its_id || '',
    ite_tag: item.ite_tag || '',
    its_description: item.its_description || '',
    labelLower: (item.label || '').toLowerCase(),
    altLower: (item.ite_alternativeidentifier || '').toLowerCase(),
    itsId: item.its_id != null && item.its_id !== '' ? String(item.its_id) : '',
  }));
}

function filterItems(items: IndexedItem[], filters: ListFilters): IndexedItem[] {
  let result = items;
  if (filters.subgroupId) {
    result = result.filter((item) => item.itsId === filters.subgroupId);
  }
  if (filters.alternative) {
    const q = filters.alternative.toLowerCase();
    result = result.filter((item) => item.altLower.includes(q));
  }
  if (filters.description) {
    const q = filters.description.toLowerCase();
    result = result.filter((item) => item.labelLower.includes(q));
  }
  return result;
}

function hasActiveFilter(filters: ListFilters): boolean {
  return Boolean(filters.subgroupId || filters.alternative || filters.description);
}

const DualListBoxPesonalizado: React.FC<DualListBoxProps> = ({
  items,
  pegarItens,
  itemsSel,
  descricao1,
  descricao2,
  enableItemFieldSearch = false,
}) => {
  const [availableItems, setAvailableItems] = useState<IndexedItem[]>(() => indexItems(items));
  const [selectedItems, setSelectedItems] = useState<IndexedItem[]>(() => indexItems(itemsSel));

  const [descriptionAvailable, setDescriptionAvailable] = useState('');
  const [descriptionSelected, setDescriptionSelected] = useState('');
  const [alternativeAvailable, setAlternativeAvailable] = useState('');
  const [alternativeSelected, setAlternativeSelected] = useState('');
  const [debouncedDescAvailable, setDebouncedDescAvailable] = useState('');
  const [debouncedDescSelected, setDebouncedDescSelected] = useState('');
  const [debouncedAltAvailable, setDebouncedAltAvailable] = useState('');
  const [debouncedAltSelected, setDebouncedAltSelected] = useState('');
  const [subgroupAvailable, setSubgroupAvailable] = useState('');
  const [subgroupSelected, setSubgroupSelected] = useState('');
  const [subgroupOptions, setSubgroupOptions] = useState<SubgroupOption[]>([]);
  const [limitAvailable, setLimitAvailable] = useState(EMPTY_LIMIT);
  const [limitSelected, setLimitSelected] = useState(EMPTY_LIMIT);
  const [selectedAvailable, setSelectedAvailable] = useState<string[]>([]);
  const [selectedSelected, setSelectedSelected] = useState<string[]>([]);

  useEffect(() => {
    if (!enableItemFieldSearch) return;
    let cancelled = false;
    loadSubgroupOptions(api).then((opts) => {
      if (!cancelled) setSubgroupOptions(opts);
    });
    return () => {
      cancelled = true;
    };
  }, [enableItemFieldSearch]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedDescAvailable(descriptionAvailable.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [descriptionAvailable]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedDescSelected(descriptionSelected.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [descriptionSelected]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedAltAvailable(alternativeAvailable.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [alternativeAvailable]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedAltSelected(alternativeSelected.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [alternativeSelected]);

  const filtersAvailable = useMemo<ListFilters>(
    () => ({
      description: debouncedDescAvailable,
      alternative: enableItemFieldSearch ? debouncedAltAvailable : '',
      subgroupId: enableItemFieldSearch ? subgroupAvailable : '',
    }),
    [debouncedDescAvailable, debouncedAltAvailable, subgroupAvailable, enableItemFieldSearch],
  );

  const filtersSelected = useMemo<ListFilters>(
    () => ({
      description: debouncedDescSelected,
      alternative: enableItemFieldSearch ? debouncedAltSelected : '',
      subgroupId: enableItemFieldSearch ? subgroupSelected : '',
    }),
    [debouncedDescSelected, debouncedAltSelected, subgroupSelected, enableItemFieldSearch],
  );

  const filteredAvailable = useMemo(
    () => filterItems(availableItems, filtersAvailable),
    [availableItems, filtersAvailable],
  );

  const filteredSelected = useMemo(
    () => filterItems(selectedItems, filtersSelected),
    [selectedItems, filtersSelected],
  );

  const activeFilterAvailable = hasActiveFilter(filtersAvailable);
  const activeFilterSelected = hasActiveFilter(filtersSelected);

  useEffect(() => {
    setLimitAvailable(activeFilterAvailable ? PAGE_SIZE : EMPTY_LIMIT);
  }, [filtersAvailable, activeFilterAvailable]);

  useEffect(() => {
    setLimitSelected(activeFilterSelected ? PAGE_SIZE : EMPTY_LIMIT);
  }, [filtersSelected, activeFilterSelected]);

  const visibleAvailable = useMemo(
    () => filteredAvailable.slice(0, limitAvailable),
    [filteredAvailable, limitAvailable],
  );

  const visibleSelected = useMemo(
    () => filteredSelected.slice(0, limitSelected),
    [filteredSelected, limitSelected],
  );

  const toggleId = (
    id: string,
    current: string[],
    setCurrent: React.Dispatch<React.SetStateAction<string[]>>,
    multi: boolean,
  ) => {
    if (multi) {
      setCurrent(
        current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
      );
      return;
    }
    setCurrent(current.includes(id) && current.length === 1 ? [] : [id]);
  };

  const handleSelectItem = () => {
    const moving = availableItems.filter((i) => selectedAvailable.includes(i.id));
    const newSelectedItems = [...selectedItems, ...moving];
    pegarItens(newSelectedItems);
    setSelectedItems(newSelectedItems);
    setAvailableItems((prev) => prev.filter((i) => !selectedAvailable.includes(i.id)));
    setSelectedAvailable([]);
  };

  const handleDeselectItem = () => {
    if (selectedSelected.length === 0) return;

    const moving = selectedItems.filter((i) => selectedSelected.includes(i.id));
    const remaining = selectedItems.filter((i) => !selectedSelected.includes(i.id));

    pegarItens(remaining);
    setAvailableItems((prev) => [...prev, ...moving]);
    setSelectedItems(remaining);
    setSelectedSelected([]);
  };

  const handleSelectAll = () => {
    pegarItens([...availableItems, ...selectedItems]);
    setSelectedItems((prev) => [...availableItems, ...prev]);
    setAvailableItems([]);
    setSelectedAvailable([]);
  };

  const handleDeselectAll = () => {
    setAvailableItems((prev) => [...prev, ...selectedItems]);
    setSelectedItems([]);
    pegarItens([]);
    setSelectedSelected([]);
  };

  const renderFooter = (
    visibleCount: number,
    totalFiltered: number,
    hasFilter: boolean,
    onLoadMore: () => void,
  ) => {
    if (totalFiltered === 0) {
      return <p className="dual-listbox__hint">Nenhum item encontrado.</p>;
    }
    if (visibleCount >= totalFiltered) {
      if (hasFilter) {
        return (
          <p className="dual-listbox__hint">
            {totalFiltered} {totalFiltered === 1 ? 'resultado' : 'resultados'}.
          </p>
        );
      }
      return null;
    }

    const remaining = totalFiltered - visibleCount;

    return (
      <div className="dual-listbox__footer">
        <p className="dual-listbox__hint">
          {hasFilter
            ? `Mostrando ${visibleCount} de ${totalFiltered} resultados.`
            : `Mostrando ${visibleCount} de ${totalFiltered}. Digite para buscar.`}
        </p>
        {hasFilter && (
          <button type="button" className="dual-listbox__load-more" onClick={onLoadMore}>
            Carregar mais ({remaining})
          </button>
        )}
      </div>
    );
  };

  const renderSearchBox = (
    description: string,
    onDescriptionChange: (v: string) => void,
    alternative: string,
    onAlternativeChange: (v: string) => void,
    subgroupValue: string,
    onSubgroupChange: (v: string) => void,
  ) => {
    if (!enableItemFieldSearch) {
      return (
        <div className="input-box-ativos dual-listbox__search-wrap">
          <input
            type="text"
            placeholder="Pesquisar..."
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            className="search-box"
          />
        </div>
      );
    }

    return (
      <div className="input-box-ativos dual-listbox__search-wrap dual-listbox__search-wrap--fields">
        <div className="dual-listbox__search-row">
          <select
            className="search-box dual-listbox__subgroup-select"
            value={subgroupValue}
            onChange={(e) => onSubgroupChange(e.target.value)}
            aria-label="Subgrupo"
          >
            <option value="">Subgrupo...</option>
            {subgroupOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Identificador alternativo..."
            value={alternative}
            onChange={(e) => onAlternativeChange(e.target.value)}
            className="search-box"
            aria-label="Identificador alternativo"
          />
        </div>
        <input
          type="text"
          placeholder="Descrição..."
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          className="search-box dual-listbox__description-input"
          aria-label="Descrição"
        />
      </div>
    );
  };

  const renderList = (
    visible: IndexedItem[],
    selectedIds: string[],
    setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>,
  ) => (
    <div className="list dual-listbox__options" role="listbox" aria-multiselectable="true">
      {visible.map((item) => {
        const isSelected = selectedIds.includes(item.id);
        return (
          <div
            key={item.id}
            role="option"
            aria-selected={isSelected}
            tabIndex={0}
            title={item.label}
            className={`option-list dual-listbox__option${isSelected ? ' dual-listbox__option--selected' : ''}`}
            onClick={(e) => {
              toggleId(item.id, selectedIds, setSelectedIds, e.ctrlKey || e.metaKey);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleId(item.id, selectedIds, setSelectedIds, e.ctrlKey || e.metaKey);
              }
            }}
          >
            {item.label}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="dual-listbox">
      <div className="listbox">
        <span className="details-list-lab">{descricao1}:</span>
        {renderSearchBox(
          descriptionAvailable,
          setDescriptionAvailable,
          alternativeAvailable,
          setAlternativeAvailable,
          subgroupAvailable,
          setSubgroupAvailable,
        )}
        {renderList(visibleAvailable, selectedAvailable, setSelectedAvailable)}
        {renderFooter(
          visibleAvailable.length,
          filteredAvailable.length,
          activeFilterAvailable,
          () => setLimitAvailable((prev) => prev + PAGE_SIZE),
        )}
      </div>

      <div className="buttons">
        <button type="button" onClick={handleSelectAll} className="btn-move">
          &gt;&gt;
        </button>
        <button type="button" onClick={handleSelectItem} className="btn-move">
          &gt;
        </button>
        <button type="button" onClick={handleDeselectItem} className="btn-move">
          &lt;
        </button>
        <button type="button" onClick={handleDeselectAll} className="btn-move">
          &lt;&lt;
        </button>
      </div>

      <div className="listbox">
        <span className="details-list-lab">{descricao2}:</span>
        {renderSearchBox(
          descriptionSelected,
          setDescriptionSelected,
          alternativeSelected,
          setAlternativeSelected,
          subgroupSelected,
          setSubgroupSelected,
        )}
        {renderList(visibleSelected, selectedSelected, setSelectedSelected)}
        {renderFooter(
          visibleSelected.length,
          filteredSelected.length,
          activeFilterSelected,
          () => setLimitSelected((prev) => prev + PAGE_SIZE),
        )}
      </div>
    </div>
  );
};

export default DualListBoxPesonalizado;
